import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Leaf } from "@phosphor-icons/react";

const BG = "https://static.prod-images.emergentagent.com/jobs/827c6d57-fb61-4c96-8e7d-b91e04529c3b/images/a0d870928ba22e771797af57d348e41dcedd9257fb714c17f5b971fd7b471c9e.png";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("gardaj.office@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // DEPLOY-2: blur the active input BEFORE navigation so iOS releases the
    // zoom state captured on focus. Combined with text-base inputs (16px) this
    // prevents zoom/viewport carryover into the dashboard.
    try { if (document.activeElement?.blur) document.activeElement.blur(); } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Login: input blur failed:", err?.message);
    }
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name || u.email}`);
      nav("/dashboard", { replace: true });
      // DEPLOY-1: iOS Safari retains scrollY across the SPA route swap; reset
      // both window and any scrollable main container so dashboard opens at top.
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const main = document.querySelector("main");
        if (main) main.scrollTop = 0;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Login: post-navigation scroll reset failed:", err?.message);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel — brand */}
      <div className="hidden md:flex md:w-[58%] relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A3A21]/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#E1A104] flex items-center justify-center">
              <Leaf size={24} weight="duotone" className="text-[#1A3A21]" />
            </div>
            <div className="font-display text-2xl tracking-tight">GARDAJ</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E1A104] mb-3">B2B Wholesale CRM</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
              Premium hemp <br /> distribution, <span className="text-[#E1A104]">organised.</span>
            </h1>
            <p className="mt-5 max-w-md text-white/80 text-sm leading-relaxed">
              Manage leads, track follow-ups and grow accounts across Europe —
              all from one lean dashboard built for sales agents.
            </p>
          </div>
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} GARDAJ DISTRIBUTION s.r.o.
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#1A3A21] flex items-center justify-center">
              <Leaf size={22} weight="duotone" className="text-[#E1A104]" />
            </div>
            <div className="font-display text-xl tracking-tight">GARDAJ CRM</div>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-1">Sign in</h2>
          <p className="text-sm text-gray-500 mb-8">
            Access your leads, tasks and activity timeline.
          </p>

          <label className="block text-sm font-medium mb-1.5">Email</label>
          <input
            type="email"
            required
            inputMode="email"
            autoComplete="email"
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // DEPLOY-2: text-base (16px) prevents iOS Safari auto-zoom on input
            // focus. Without this, mobile login zooms in and the dashboard
            // inherits the zoomed/clipped viewport.
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#1A3A21] focus:border-[#1A3A21] mb-4"
            placeholder="you@company.com"
          />

          <label className="block text-sm font-medium mb-1.5">Password</label>
          <input
            type="password"
            required
            autoComplete="current-password"
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#1A3A21] focus:border-[#1A3A21] mb-6"
            placeholder="••••••••"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-[#1A3A21] hover:bg-[#142C19] text-white font-semibold py-2.5 rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>

          {process.env.REACT_APP_SHOW_DEMO_CREDS === "true" && (
            <div className="mt-8 p-4 bg-[#F9FAFB] border border-gray-200 rounded-md text-xs text-gray-600"
              data-testid="demo-credentials-block">
              <div className="font-semibold text-gray-700 mb-1.5">Demo credentials</div>
              <div>Admin: gardaj.office@gmail.com / Gardaj2026!</div>
              <div>Agent: marco.rossi@gardaj.com / Agent2026!</div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
